import { TruFinVaultAbi } from '../abis/trufin'
import type { LstWithdrawalRequest } from '../types'
import { toBigInt, type LstWithdrawalReader } from './shared'

/**
 * TruFin TruMATIC — log-free via caller-supplied
 * `{ validator, nonce }` pairs.
 *
 * Unlike Mantle/Puffer (single-bigint id), TruFin's natural request
 * unit is `(validator, unbondNonce)` — both arguments to the claim
 * function `withdrawClaim(unbondNonce, validator)`. The reader
 * filters `knownIds` to objects with `validator` + `nonce`.
 *
 * Per pair: multicall `withdrawals(validator, nonce)` +
 * `isClaimable(nonce, validator)`. `withdrawals` also returns the
 * recipient — used as a defensive filter against stale persisted
 * IDs from other accounts.
 *
 * Returns `[]` when no pairs are supplied.
 */
export const readerTruFin: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry, knownIds) => {
    if (!entry.withdrawalContract && !entry.lst) return []
    const vault = entry.withdrawalContract ?? entry.lst
    if (!knownIds || knownIds.length === 0) return []

    const pairs = knownIds.filter(
      (v): v is { validator: string; nonce: bigint } =>
        typeof v === 'object' && v !== null && 'validator' in v && 'nonce' in v,
    )
    if (pairs.length === 0) return []

    const calls = pairs.flatMap((p) => [
      {
        address: vault,
        name: 'withdrawals',
        params: [p.validator, p.nonce],
      },
      {
        address: vault,
        name: 'isClaimable',
        params: [p.nonce, p.validator],
      },
    ])
    const results = (await multicallRetry({
      chain: chainId,
      calls: calls as unknown as never[],
      abi: calls.map(() => TruFinVaultAbi) as unknown as never,
    })) as unknown[]

    const out: LstWithdrawalRequest[] = []
    const lcUser = user.toLowerCase()
    for (let i = 0; i < pairs.length; i++) {
      const w = results[i * 2] as
        | { user?: unknown; amount?: unknown }
        | unknown[]
        | undefined
      const claimable = Boolean(results[i * 2 + 1])
      if (!w) continue
      const recipient = Array.isArray(w)
        ? (w[0] as string | undefined)
        : (w.user as string | undefined)
      if (typeof recipient === 'string' && recipient.toLowerCase() !== lcUser) {
        continue
      }
      const amount = Array.isArray(w) ? toBigInt(w[1]) : toBigInt(w.amount)
      if (amount === undefined || amount === 0n) continue
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        // Composite id — same shape as the input.
        requestId: `${pairs[i].validator}:${pairs[i].nonce.toString()}`,
        amountUnderlying: amount.toString(),
        status: claimable ? 'claimable' : 'pending',
      })
    }
    return out
  },
}
